// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Common utility functions for client-side CSV export.
 * Used across the application for generating and downloading CSV files.
 */
import { DateTime } from 'luxon';

/**
 * Escapes a string value for CSV format.
 * Wraps in quotes and escapes any internal quotes.
 *
 * @param value - The string value to escape
 * @returns A CSV-safe string wrapped in quotes
 */
export function escapeCSV(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  // Escape quotes by doubling them, then wrap in quotes
  return `"${value.replace(/"/g, '""')}"`;
}

/**
 * Formats a DateTime value for CSV export as ISO date string.
 *
 * @param date - The DateTime to format
 * @returns ISO date string (YYYY-MM-DD) or empty string if null
 */
export function formatDateForCSV(date: DateTime | null | undefined): string {
  if (!date) {
    return '';
  }
  // ToISODate() is guaranteed to return string when date.isValid is true
  return date.toISODate();
}

/**
 * Formats a numeric string value for CSV export.
 * Returns the value as-is or empty string if null/undefined.
 *
 * @param value - The string number to format
 * @returns The value or empty string if null
 */
export function formatAmountForCSV(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  return value;
}

/**
 * Triggers a browser download for CSV content.
 * Creates a Blob from the CSV string and initiates the download.
 *
 * Note: We avoid appending the link to document.body to prevent triggering
 * MutationObservers from browser extensions (e.g., text highlighters) which
 * can cause significant performance issues.
 *
 * @param csvContent - The CSV content as a string
 * @param filename - The filename for the download (should include .csv extension)
 */
export function triggerCSVDownload(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);

  // Click without appending to DOM to avoid triggering extension observers
  link.click();

  // Delay URL cleanup to ensure browser has fully processed the download
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Generates a timestamped filename for CSV exports.
 *
 * @param prefix - The prefix for the filename (e.g., 'inventory-assets')
 * @param asOfDate - Optional as-of date to include in the filename
 * @returns A formatted filename string with timestamp
 */
export function generateCSVFilename(
  prefix: string,
  asOfDate?: DateTime
): string {
  const timestamp = DateTime.now().toFormat('yyyy-MM-dd_HHmm');

  if (asOfDate) {
    const dateStr = asOfDate.toFormat('yyyy-MM-dd');
    return `${prefix}_as_of_${dateStr}_${timestamp}.csv`;
  }

  return `${prefix}_${timestamp}.csv`;
}

/**
 * Converts an array of row data into CSV content string.
 * Joins headers and rows with newlines.
 *
 * @param headers - Array of header column names
 * @param rows - Array of row arrays (each row is an array of cell values)
 * @returns Complete CSV content as a string
 */
export function buildCSVContent(headers: string[], rows: string[][]): string {
  return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
}
